import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  DisabledReason,
  FormValueControl,
  ValidationError,
  WithOptionalFieldTree,
} from '@angular/forms/signals';
import { ValidationType } from '@softline/core';
import { DateRangePickerComponent } from '@softline/ui-core/date-input';
import { DateRange, OverlayComponent, TranslatePipe } from '@softline/ui-core';
import { FormatPipe } from '@softline/ui-core';
import { DateParser } from '@softline/ui-core';
import { ValidationContainerComponent } from '@softline/ui-core';
import { IconComponent } from '@softline/ui-core';
import { LabelComponent } from '@softline/ui-core';
import {
  DateRangeInputSize,
  DateRangeInputValue,
} from './date-range-input.types';

let nextDateRangeInputId = 0;

/**
 * Single signal-forms control for a date range. Renders two inline endpoint
 * fields (from — to) sharing one bordered surface; each endpoint has its own
 * calendar icon that toggles one shared DS dropdown picker, started on that
 * endpoint. Implements
 * {@link FormValueControl} so it can be bound through the `[formField]` API of
 * `@angular/forms/signals`, and {@link ControlValueAccessor} for reactive forms
 * compatibility.
 *
 * The value is a {@link DateRangeInputValue} (`{ from, to }` with independently
 * nullable endpoints) and is normalized to `null` when both endpoints are empty.
 */
@Component({
  selector: 'soft-date-range-input, soft-ds-date-range-input',
  templateUrl: './date-range-input.component.html',
  styleUrl: './date-range-input.component.css',
  imports: [
    CdkOverlayOrigin,
    TranslatePipe,
    FormatPipe,
    IconComponent,
    LabelComponent,
    OverlayComponent,
    DateRangePickerComponent,
    ValidationContainerComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeInputComponent),
      multi: true,
    },
  ],
  host: {
    '[class]': 'hostClasses()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeInputComponent
  implements ControlValueAccessor, FormValueControl<DateRangeInputValue | null>
{
  private readonly dateParser = inject(DateParser) as DateParser<string>;
  private readonly fieldId = nextDateRangeInputId++;

  private onChange: (value: DateRangeInputValue | null) => void = () => void 0;
  private onTouch: () => void = () => void 0;

  /** Raw text currently typed into each endpoint (before parsing). */
  protected readonly fromInput = signal<string | null>(null);
  protected readonly toInput = signal<string | null>(null);

  // FormValueControl contract — wired up by the signal-forms FieldDirective.
  readonly value = model<DateRangeInputValue | null>(null);
  readonly open = model(false);
  readonly errors = input<readonly WithOptionalFieldTree<ValidationError>[]>([]);
  readonly disabled = input<boolean>(false);
  readonly disabledReasons = input<readonly WithOptionalFieldTree<DisabledReason>[]>([]);
  readonly readonly = input<boolean>(false);
  readonly hidden = input(false);
  readonly invalid = input<boolean>(false);
  readonly pending = input<boolean>(false);
  readonly touched = model<boolean>(false);
  readonly dirty = input<boolean>(false);
  readonly name = input<string>('');
  readonly required = input<boolean>(false);

  readonly label = input<string | null | undefined>(null);
  readonly inputId = input<string | null>(null);
  readonly size = input<DateRangeInputSize>('lg');
  readonly format = input<string>('DATE.DEFAULT');
  readonly fromPlaceholder = input<string>(
    '#UI_CORE.COMPONENTS.DATE_INPUT.PLACEHOLDER'
  );
  readonly toPlaceholder = input<string>(
    '#UI_CORE.COMPONENTS.DATE_INPUT.PLACEHOLDER'
  );
  readonly changeTrigger = input<'blur' | 'input'>('blur');

  // Matches the existing date-range endpoint API (`blur` output); the styled
  // field is not itself focusable, so there is no native blur to collide with.
  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly blur = output<void>();

  protected readonly from = computed(() => this.value()?.from ?? null);
  protected readonly to = computed(() => this.value()?.to ?? null);

  protected readonly activeField = signal<'from' | 'to'>('from');
  protected readonly currentRange = computed<DateRange | null>(() => {
    const from = this.from();
    const to = this.to();
    return from && to ? { from, to } : null;
  });

  protected readonly canOpenPicker = computed(
    () => !this.readonly() && !this.disabled()
  );

  readonly validationType = computed<ValidationType | null>(() => {
    const types = [null, 'info', 'success', 'warning', 'error'];
    return this.errors().reduce(
      (acc, val) => {
        const accIndex = types.indexOf(acc ?? null);
        const valIndex = types.indexOf(val.kind);
        if (valIndex > accIndex) return val.kind as ValidationType;
        return acc;
      },
      null as ValidationType | null
    );
  });

  protected readonly hasErrors = computed(() => this.errors().length > 0);
  protected readonly isInvalid = computed(
    () => this.invalid() || this.validationType() === 'error'
  );

  private readonly fallbackInputId = `soft-ds-date-range-input-${this.fieldId}`;
  protected readonly controlId = computed(
    () => this.inputId() ?? this.fallbackInputId
  );
  protected readonly fromControlId = computed(() => `${this.controlId()}-from`);
  protected readonly toControlId = computed(() => `${this.controlId()}-to`);

  protected readonly hostClasses = computed(
    () =>
      `soft-date-range-input-host soft-ds-date-range-input-host size-${this.size()}` +
      (this.hidden() ? ' !hidden' : '') +
      (this.open() ? ' is-open' : '') +
      (this.disabled() ? ' is-disabled' : '') +
      (this.readonly() ? ' readonly is-readonly' : '') +
      (this.pending() ? ' is-pending' : '') +
      (this.from() || this.to() ? ' is-filled' : '') +
      (this.isInvalid() ? ' is-invalid' : '') +
      (this.validationType() ? ` state-${this.validationType()}` : '')
  );

  registerOnChange(fn: (value: DateRangeInputValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(): void {
    // disabled is driven through the `disabled` input by the FieldDirective and
    // by reactive-forms `[disabled]`; nothing extra to do here.
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  writeValue(obj: any): void {
    this.value.set(this.normalize(obj));
    this.fromInput.set(null);
    this.toInput.set(null);
  }

  protected onEndpointInput(field: 'from' | 'to', text: string | null): void {
    if (field === 'from') this.fromInput.set(text);
    else this.toInput.set(text);
    if (this.changeTrigger() === 'input') this.setEndpoint(field, text, true);
  }

  protected onEndpointChange(field: 'from' | 'to', text: string | null): void {
    if (this.changeTrigger() === 'blur') this.setEndpoint(field, text, true);
    if (field === 'from' && this.from()) this.fromInput.set(null);
    if (field === 'to' && this.to()) this.toInput.set(null);
  }

  protected onEndpointBlur(): void {
    this.onTouch();
    this.touched.set(true);
    this.blur.emit();
  }

  /** The calendar icon toggles the shared dropdown, started on its endpoint. */
  protected togglePicker(field: 'from' | 'to'): void {
    if (!this.canOpenPicker()) return;
    this.onTouch();
    this.touched.set(true);
    if (this.open() && this.activeField() === field) {
      this.open.set(false);
      return;
    }
    this.activeField.set(field);
    this.open.set(true);
  }

  protected onPickerValueChange(range: DateRange | null): void {
    this.commit({ from: range?.from ?? null, to: range?.to ?? null });
    this.fromInput.set(null);
    this.toInput.set(null);
    this.open.set(false);
  }

  private setEndpoint(field: 'from' | 'to', text: string | null, parse: boolean): void {
    const parsed = !text ? null : parse ? this.dateParser.parse(text) ?? null : text;
    const next: DateRangeInputValue = {
      from: field === 'from' ? parsed : this.from(),
      to: field === 'to' ? parsed : this.to(),
    };
    this.commit(next);
  }

  private commit(value: DateRangeInputValue): void {
    const normalized = this.normalize(value);
    if (this.equals(normalized, this.value())) return;

    this.value.set(normalized);
    this.onChange(normalized);
  }

  private normalize(value: unknown): DateRangeInputValue | null {
    if (!value || typeof value !== 'object') return null;
    const range = value as Partial<DateRangeInputValue>;
    const from = range.from ?? null;
    const to = range.to ?? null;
    if (!from && !to) return null;
    return { from, to };
  }

  private equals(
    a: DateRangeInputValue | null,
    b: DateRangeInputValue | null
  ): boolean {
    return (a?.from ?? null) === (b?.from ?? null) && (a?.to ?? null) === (b?.to ?? null);
  }
}
